/**
 * BookingsTabs — Bookings / Reservations / Tree replacements under the fill
 * bar (BON-490 AC2). One list shows at a time; the tree-replacements tab is
 * always present, even with no pickups, so staff can add the first one. It
 * reads "Trees" on mobile to save width.
 *
 * Badges are SEATS, matching the KPI row above them (Bookings/Reservations
 * count rows in their own lists, which can differ from seats — a booking can
 * hold >1 ticket — so the badges deliberately read `roster.confirmedAttendees`
 * / `roster.reservedSeats`, not `.length`). Tree replacements take no seat, so
 * its badge stays the pickup COUNT.
 *
 * Roving tabindex (WAI-ARIA tabs pattern): only the active tab is in the Tab
 * order; ArrowLeft/Right/Home/End move focus within the tablist and select
 * the newly-focused tab (automatic activation), wrapping at both ends.
 */
import type { TabId } from './bookingsCardTypes';
import { BookingsTab } from './BookingsTab';
import { isTabNavKey, nextTabIndex } from './bookingsTabsKeyboard';

export interface BookingsTabsProps {
  activeTab: TabId;
  onSelectTab: (tab: TabId) => void;
  bookingsSeats: number;
  reservationsSeats: number;
  treeReplacementCount: number;
  isMobile: boolean;
}

interface TabDef {
  id: TabId;
  label: string;
  count: number;
}

function tabDefs(props: BookingsTabsProps): TabDef[] {
  const { bookingsSeats, reservationsSeats, treeReplacementCount, isMobile } = props;
  return [
    { id: 'bookings', label: 'Bookings', count: bookingsSeats },
    { id: 'reservations', label: 'Reservations', count: reservationsSeats },
    {
      id: 'tree-replacements',
      label: isMobile ? 'Trees' : 'Tree replacements',
      count: treeReplacementCount,
    },
  ];
}

/** Move focus (and selection) to the next tab per the pressed arrow/Home/End
 *  key. No-ops on any other key, or when focus isn't currently on a tab. */
function handleTablistKeyDown(
  e: React.KeyboardEvent<HTMLDivElement>,
  onSelectTab: (tab: TabId) => void,
) {
  if (!isTabNavKey(e.key)) return;
  const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  const currentIndex = buttons.findIndex((b) => b === document.activeElement);
  if (currentIndex === -1) return;
  e.preventDefault();
  const next = buttons[nextTabIndex(e.key, currentIndex, buttons.length)];
  next.focus();
  onSelectTab(next.dataset.tabId as TabId);
}

export function BookingsTabs(props: BookingsTabsProps) {
  const { activeTab, onSelectTab, isMobile } = props;
  return (
    <div
      role="tablist"
      onKeyDown={(e) => handleTablistKeyDown(e, onSelectTab)}
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2,
        borderBottom: '1px solid var(--nb-border)',
        overflowX: isMobile ? 'auto' : 'visible',
      }}
    >
      {tabDefs(props).map((t) => (
        <BookingsTab
          key={t.id}
          id={t.id}
          label={t.label}
          count={t.count}
          active={t.id === activeTab}
          onClick={() => onSelectTab(t.id)}
        />
      ))}
    </div>
  );
}
